import React, { useEffect, useRef } from "react";
import { useLang } from "@/i18n";
import { Star } from "lucide-react";

const TP_BUSINESS_ID = process.env.REACT_APP_TRUSTPILOT_BUSINESS_ID;
const TP_TEMPLATE_ID = process.env.REACT_APP_TRUSTPILOT_TEMPLATE_ID || "5419b6ffb0d04a076446a9af";
const TP_DOMAIN = "mimisilver.ch";

const fallback = {
  it: [
    { name: "Giulia M.", text: "Spedizione velocissima e gioiello bellissimo. Confezione regalo curata nei minimi dettagli." },
    { name: "Marco R.", text: "Acquisto sicuro e qualità eccellente. Tornerò sicuramente." },
    { name: "Elena T.", text: "Servizio clienti impeccabile, mi hanno aiutata a scegliere il regalo perfetto." },
  ],
  de: [
    { name: "Sandra K.", text: "Sehr schnelle Lieferung und ein wunderschönes Schmuckstück. Liebevolle Geschenkverpackung." },
    { name: "Thomas B.", text: "Sicherer Kauf und ausgezeichnete Qualität. Komme gerne wieder." },
    { name: "Nina W.", text: "Tadelloser Kundenservice, man half mir, das perfekte Geschenk zu finden." },
  ],
  fr: [
    { name: "Camille D.", text: "Livraison ultra rapide et bijou magnifique. Emballage cadeau soigné." },
    { name: "Luc P.", text: "Achat sécurisé et qualité excellente. Je reviendrai." },
    { name: "Sophie L.", text: "Service client impeccable, on m'a aidée à choisir le cadeau parfait." },
  ],
  en: [
    { name: "Emma S.", text: "Super fast delivery and a gorgeous piece. Beautiful gift packaging." },
    { name: "James R.", text: "Secure purchase and excellent quality. I'll definitely be back." },
    { name: "Olivia T.", text: "Impeccable customer service, they helped me pick the perfect gift." },
  ],
};

const Stars = () => (
  <div className="flex gap-0.5 mb-4">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="w-4 h-4 fill-[#00b67a] text-[#00b67a]" />
    ))}
  </div>
);

export const Reviews = () => {
  const { t, lang } = useLang();
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!TP_BUSINESS_ID) return;
    const scriptId = "trustpilot-widget-script";
    const ensureWidget = () => {
      if (window.Trustpilot && widgetRef.current) {
        window.Trustpilot.loadFromElement(widgetRef.current, true);
      }
    };
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
      s.async = true;
      s.onload = ensureWidget;
      document.head.appendChild(s);
    } else {
      ensureWidget();
    }
  }, []);

  const reviews = fallback[lang] || fallback.en;

  return (
    <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 bg-[#FAF9F6]" data-testid="reviews-section">
      <div className="text-center mb-14">
        <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A1F2C]">{t("reviews_title")}</h2>
        <p className="text-sm text-[#6B7280] mt-2 flex items-center justify-center gap-2">
          <Star className="w-4 h-4 fill-[#00b67a] text-[#00b67a]" /> {t("reviews_sub")}
        </p>
      </div>

      {TP_BUSINESS_ID ? (
        <div
          ref={widgetRef}
          className="trustpilot-widget"
          data-locale={`${lang}-${lang === "en" ? "GB" : lang.toUpperCase()}`}
          data-template-id={TP_TEMPLATE_ID}
          data-businessunit-id={TP_BUSINESS_ID}
          data-style-height="240px"
          data-style-width="100%"
          data-testid="trustpilot-widget"
        >
          <a href={`https://www.trustpilot.com/review/${TP_DOMAIN}`} target="_blank" rel="noopener noreferrer">Trustpilot</a>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {reviews.map((r, i) => (
            <div key={i} data-testid={`review-card-${i}`} className="bg-white border border-[#EAE6E1] p-8">
              <Stars />
              <p className="text-base text-[#1A1F2C] leading-relaxed mb-5">"{r.text}"</p>
              <span className="text-xs tracking-luxe uppercase text-[#6B7280]">{r.name}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
